import { Reveal } from "@components/ui";

interface FeedbackCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  index: number;
}

function FeedbackCard({ icon, title, description, index }: FeedbackCardProps) {
  const watermark = String(index + 1).padStart(2, "0");

  return (
    <div className="group relative w-full rounded-[12px] md:rounded-[24px] bg-white flex flex-col items-start p-5 md:pt-8 md:px-8 md:pb-6 lg:pt-10 lg:px-10 lg:pb-8 overflow-hidden hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300 border border-transparent hover:border-[#e8e9ed]">
      {/* Watermark number */}
      <span className="absolute top-4 right-5 md:top-6 md:right-7 lg:top-8 lg:right-9 text-[48px] md:text-[64px] lg:text-[80px] font-bold text-[#1c1c1c]/[0.04] leading-none font-albert-sans select-none pointer-events-none">
        {watermark}
      </span>

      <div className="self-stretch flex-1 flex flex-col justify-between gap-6 lg:gap-8">
        <div className="self-stretch flex flex-col items-start gap-5 lg:gap-7">
          {/* Tinted icon container */}
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-[14px] bg-[#f24d2b]/[0.08] group-hover:bg-[#f24d2b] flex items-center justify-center transition-colors duration-300 text-[#f24d2b] group-hover:text-white">
            {icon}
          </div>
          <h3 className="self-stretch text-sm md:text-lg lg:text-[28px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans leading-[1.2]">
            {title}
          </h3>
        </div>
        <p className="self-stretch text-[14px] md:text-base lg:text-[18px] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.6]">
          {description}
        </p>
      </div>
    </div>
  );
}

const ImproveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20V10" />
    <path d="M18 20V4" />
    <path d="M6 20v-4" />
  </svg>
);

const TrackIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const AccountabilityIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const FutureIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const cards: Omit<FeedbackCardProps, "index">[] = [
  {
    icon: <ImproveIcon />,
    title: "Improve Our Services",
    description:
      "Your feedback directly helps us enhance our gas supply, maintenance, and customer service operations across the UAE.",
  },
  {
    icon: <TrackIcon />,
    title: "Track Your Complaint",
    description:
      "Every complaint receives a unique reference number so you can track its progress from submission to resolution.",
  },
  {
    icon: <AccountabilityIcon />,
    title: "Ensure Accountability",
    description:
      "We take every piece of feedback seriously and hold our teams accountable for delivering quality service.",
  },
  {
    icon: <FutureIcon />,
    title: "Shape Our Future",
    description:
      "Your suggestions help us plan better services, introduce new features, and improve overall customer experience.",
  },
];

export function WhyShareFeedbackSection() {
  return (
    <section className="w-full bg-white overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 lg:gap-16">
        {/* Badge + Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 md:gap-5">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Why It Matters
            </span>
            <h2 className="text-center text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Why Share Your Feedback?
            </h2>
          </div>
        </Reveal>

        {/* Cards Grid */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((card, index) => (
            <Reveal key={index} direction="up" delay={100 + index * 100}>
              <FeedbackCard {...card} index={index} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
